import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperPaper from 'assets/themes/wrappers/Theme.Wrapper.Paper';
import { sum } from 'lodash';
import AccountingItemTypeCode from 'models/enums/AccountingItemTypeCode';
import { useContractRefundProvider } from '../../Sales.ContractRefund.Hooks';

const getPayeeName = (lang: ReturnType<typeof getLocales>['lang'], payee: Model.IContractRefundPayee): string => {
  if (!payee) return lang.notAvailable;
  const name = payee.dealer?.name ?? payee.lender?.name ?? payee.client?.name ?? payee.payee?.name;
  const primary = payee.isPrimary ? ` (${lang.primaryPayee})` : '';
  return `${name ?? lang.notAvailable}${primary}`;
};

const getPayeeType = (lang: ReturnType<typeof getLocales>['lang'], payee: Model.IContractRefundPayee): string => {
  if (!payee) return lang.notAvailable;
  return payee.dealer || payee.dealerId
    ? lang.dealer
    : payee.lender || payee.lenderId
      ? lang.lender
      : payee.client || payee.clientId
        ? lang.client
        : lang.payee;
};

const DealerPaymentControlsTable = ({
  title,
  rows,
  payees,
}: {
  title: string;
  rows: Model.IContractRefundDealerPaymentControl[];
  payees: Model.IContractRefundPayee[];
}) => {
  const { lang, translate } = getLocales();
  const total = sum(rows.map((row) => row.allocatedAmountCAD ?? 0));

  return (
    <>
      <h3>{title.toLocaleUpperCase()}</h3>
      {!rows.length ? (
        <ErrorWrapper children={lang.noInfo} />
      ) : (
        <table className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.payee}</th>
              <th>{lang.payeeType}</th>
              <th>{lang.responsibilityType}</th>
              <th>{lang.paymentPurpose}</th>
              <th>{lang.accountingItemType}</th>
              <th>{lang.fundingSource}</th>
              <th style={{ textAlign: 'center' }}>{lang.allocatedAmount}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={rows}
              render={({ data: row, index }) => {
                const payee = payees.find((it) => it.id === row.contractRefundPayeeId) ?? row.contractRefundPayee;
                return (
                  <tr key={row.id || index}>
                    <td>{getPayeeName(lang, payee)}</td>
                    <td>{getPayeeType(lang, payee)}</td>
                    <td>{row.responsibilityTypeCode ? translate(row.responsibilityTypeCode) : lang.notAvailable}</td>
                    <td>{row.paymentPurposeCode ? translate(row.paymentPurposeCode) : lang.notAvailable}</td>
                    <td>{row.accountingItemTypeCode ? translate(row.accountingItemTypeCode) : lang.none}</td>
                    <td>{row.fundingSourceCode ? translate(row.fundingSourceCode) : lang.notAvailable}</td>
                    <td style={{ textAlign: 'center' }}>
                      <FormatValue value={`$${row.allocatedAmountCAD ?? 0}`} />
                    </td>
                  </tr>
                );
              }}
            />
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6} style={{ fontWeight: 'bold' }}>
                {lang.total.toLocaleUpperCase()}
              </td>
              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                <FormatValue value={`$${total}`} />
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </>
  );
};

const SalesContractRefundInfoDealerPaymentControls = () => {
  const { lang } = getLocales();
  const { selected, dealerPaymentControlsApi } = useContractRefundProvider();
  const payees = selected?.contractRefundPayees ?? [];
  const rows = dealerPaymentControlsApi?.payload?.data ?? [];
  const costRows = rows.filter((row) => row.accountingItemTypeCode === AccountingItemTypeCode.AccountPayable);
  const profitRows = rows.filter((row) => row.accountingItemTypeCode !== AccountingItemTypeCode.AccountPayable);

  return (
    <FormWrapper class={ThemeWrapperPaper}>
      <div style={{ fontSize: Theme.FontSize.L, fontWeight: 'bold' }}>{lang.dealerPaymentControls}</div>
      &nbsp;
      <DealerPaymentControlsTable title={lang.dealerCostControls} rows={costRows} payees={payees} />
      &nbsp;
      <DealerPaymentControlsTable title={lang.dealerProfitControls} rows={profitRows} payees={payees} />
    </FormWrapper>
  );
};

export default SalesContractRefundInfoDealerPaymentControls;
